import { TicketActionsEnum, TicketAgentRole, TicketMilestoneEvents, TicketStatus } from '../pages/Ticket/TicketController';
import { serviceRequest, wallet } from '../types';
import { getLookupWitHDefaultValue } from '../utils/common';
import { billingApprovalReasonOptions } from '../pages/CustomerBilling/CustomerBillingController';

const INITIAL_STATE = {
    error: undefined,
    serviceRequestList: [],
    serviceRequestCount: {},
    eligibleAgents: [],
    ticketProgress: [],
    serviceRequestData: {},
    serviceRequestPayments: {},
    serviceRequestMilestones: {},
    showProgress: false,
    csvHeader: [],
    paymentList: [],
    triggeredServiceRequestList: [],
    eligibleEngineers: [],
    serviceRequestFilterPayload: {},
    serviceRequestHistory: {},
    serviceRequestFilterList: {},
    ticketPermissions: [],
    supportTrackServiceRequest: {},
    paymentFilterPayload: {},
    serviceRequestCountByStatus: [],
    visitHistory: {},
    paymentCount: 0,
    billingRemarks: {},
    billingRemarksFilterPayload: undefined,
};


export const statusColors = getLookupWitHDefaultValue({
    lookup: {
        [TicketStatus.OPEN]: {
            bgColor: 'bg-blue-50 dark:bg-blue-900/40',
            borderColor: 'border-blue-100 dark:border-blue-700/50',
            hoverBg: 'hover:bg-blue-100/70 dark:hover:bg-blue-800/90',
        },
        [TicketStatus.IN_PROGRESS]: {
            bgColor: 'bg-amber-50 dark:bg-amber-900/60',
            borderColor: 'border-amber-100 dark:border-amber-700/50',
            hoverBg: 'hover:bg-amber-100/70 dark:hover:bg-amber-800/90',
        },
        [TicketStatus.ON_HOLD]: {
            bgColor: 'bg-red-50 dark:bg-red-900/60',
            borderColor: 'border-red-100 dark:border-red-700/50',
            hoverBg: 'hover:bg-red-100/70 dark:hover:bg-red-800/90',
        },
        [TicketStatus.CLOSED]: {
            bgColor: 'bg-green-50 dark:bg-green-900/60',
            borderColor: 'border-green-100 dark:border-green-700/50',
            hoverBg: 'hover:bg-green-100/70 dark:hover:bg-green-800/90',
        },
        [TicketStatus.TRIGGERED]: {
            bgColor: 'bg-purple-50 dark:bg-purple-900/60',
            borderColor: 'border-purple-100 dark:border-purple-700/50',
            hoverBg: 'hover:bg-purple-100/70 dark:hover:bg-purple-800/90',
        },
        [TicketStatus.PLANNED]: {
            bgColor: 'bg-lightblue-50 dark:bg-lightblue-900/60',
            borderColor: 'border-lightblue-100 dark:border-lightblue-700/50',
            hoverBg: 'hover:bg-lightblue-100/70 dark:hover:bg-lightblue-800/90',
        },
        [TicketStatus.SIGNOFF_REQUESTED]: {
            bgColor: 'bg-lime-50 dark:bg-lime-900/60',
            borderColor: 'border-lime-100 dark:border-lime-700/50',
            hoverBg: 'hover:bg-lime-100/70 dark:hover:bg-lime-800/90',
        }
    },
    defaultValue: () => ({
        bgColor: 'bg-blue-50 dark:bg-blue-900/40',
        borderColor: 'border-blue-100 dark:border-blue-700/50',
        hoverBg: 'hover:bg-blue-100/70 dark:hover:bg-blue-800/90',
    }),
});

const serviceRequestReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {

        case serviceRequest.GET_SERVICE_REQUEST_LIST_SUCCESS: {
            const response = action.payload;
            if (response.data) {
                response.data = response.data.map(item => {
                    return {
                        ...item,
                        rowColor: statusColors[item.status],
                    }
                })
            }

            const ticketPermissions = new Set();

            response.data.forEach(({ actions = [] }) => {
                const canEdit = actions.find(action => action.allowed && action.action === TicketActionsEnum.EDIT_TICKET);
                const canDelete = actions.find(action => action.allowed && action.action === TicketActionsEnum.DELETE_TICKET);
                const canBroadcast = actions.find(action => action.allowed && action.action === TicketActionsEnum.BROADCAST_TICKET);
                const canAssign = actions.find(action => action.allowed && action.action === TicketActionsEnum.ASSIGN_TICKET);
                const canModifyExecutionDate = actions.find(action => action.allowed && action.action === TicketActionsEnum.MODIFY_EXECUTION_DATE);

                if (canEdit) ticketPermissions.add(TicketActionsEnum.EDIT_TICKET);
                if (canDelete) ticketPermissions.add(TicketActionsEnum.DELETE_TICKET);
                if (canBroadcast) ticketPermissions.add(TicketActionsEnum.BROADCAST_TICKET);
                if (canAssign) ticketPermissions.add(TicketActionsEnum.ASSIGN_TICKET);
                if (canModifyExecutionDate) ticketPermissions.add(TicketActionsEnum.MODIFY_EXECUTION_DATE);
            });

            // Scrolled lists ask for the next page to be added to the ones already shown.
            if (action.append) {
                const alreadyLoaded = state.serviceRequestList?.data || [];
                state.ticketPermissions.forEach((permission) => ticketPermissions.add(permission));
                return {
                    ...state,
                    serviceRequestList: { ...response, data: [...alreadyLoaded, ...response.data] },
                    ticketPermissions: Array.from(ticketPermissions)
                };
            }

            return {
                ...state,
                serviceRequestList: response,
                ticketPermissions: Array.from(ticketPermissions)
            };
        }

        case serviceRequest.SET_BILLING_REMARKS_FILTER_PAYLOAD: {
            return {
                ...state,
                billingRemarksFilterPayload: action.payload
            };
        }

        case serviceRequest.GET_BILLING_REMARKS_SUCCESS: {
            const { data = [], metadata = {}, page } = action.payload;
            // Pages after the first are appended, the list is scrolled rather than paginated.
            const alreadyLoaded = page > 1 ? state.billingRemarks?.data || [] : [];
            return {
                ...state,
                billingRemarks: { data: [...alreadyLoaded, ...data], metadata }
            };
        }

        case serviceRequest.GET_BILLING_REMARKS_FAILURE: {
            return {
                ...state,
                billingRemarks: { data: state.billingRemarks?.data || [], metadata: state.billingRemarks?.metadata || {} }
            };
        }

        case serviceRequest.GET_SERVICE_REQUEST_COUNT_V2_SUCCESS: {
            const count = action.payload;
            const updatedData = { ...state.serviceRequestList };
            if (!updatedData.metadata) {
                updatedData.metadata = {};
            };
            updatedData.metadata.total_count = count;
            return {
                ...state,
                serviceRequestList: updatedData
            };
        }

        case serviceRequest.GET_EXTRA_DETAILS_BY_IDS_SUCCESS: {
            const { additonalData, newLoggedUserData } = action.payload;
            const isLoginUserIsSP = newLoggedUserData.user.roles.find((elem) => elem.label === 'SP');

            const { assets, milestones, remarks, trackingDetails, externalTicketIds, additionalApprovedAmount, ticket_payment_ratios, milestoneWiseAdditionalApprovedAmount, invoices = [] } = additonalData;
            const data = state.serviceRequestList.data;
            const serviceRequestData = { ...state.serviceRequestData };
            data.forEach(item => {
                const filterCurrentAssets = assets.filter(asset => asset.ticket_id === item.id);
                const filterCurrentMilestones = milestones.filter(milestone => milestone.ticket_id === item.id);
                const filterCurrentRemarks = remarks.filter(remark => remark.ticket_id === item.id) || [];
                const findTrackingDetails = trackingDetails.find(track => track.ticket_id === item.id);
                const findExternalTicketId = externalTicketIds.find((t) => t.ticket_id === item.id) || {};
                const findAdditionalApprovedAmount = additionalApprovedAmount.find((t) => t.ticket_id === item.id) || {};
                const findInvoices = invoices?.find((t) => t.id === item.id);
                if (findInvoices && findInvoices.invoice) {
                    item.invoice = findInvoices.invoice
                }
                item.assets = filterCurrentAssets;

                // Milestone Filter Condition
                if (isLoginUserIsSP && filterCurrentMilestones?.length > 0) {
                    const findMilestonesOfCurrentUserAccepted = item.agents.filter((ag) => ag.agent.id === newLoggedUserData.user.id && ag.role === 'SP').map((elem) => elem.milestone_id) || [];
                    item.milestones = filterCurrentMilestones.filter((mile) => findMilestonesOfCurrentUserAccepted.includes(mile.id));
                } else {
                    item.milestones = filterCurrentMilestones;
                }
                item.remarks = filterCurrentRemarks.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                item.tracking_details = findTrackingDetails;
                item.external_ticket_id = findExternalTicketId?.external_ticket_id;
                item.additional_amount = findAdditionalApprovedAmount;
                let mwaaa = [];
                if (milestoneWiseAdditionalApprovedAmount.length > 0) {
                    mwaaa = milestoneWiseAdditionalApprovedAmount.filter((elem) => elem.ticket_id === item.id);
                }
                item.milestone_wise_additional_approved_amount = mwaaa;
                const distanceTravelled = item.remarks.filter(remark => remark.action === TicketMilestoneEvents.MILESTONE_ENGINEER_AT_SITE);
                if (distanceTravelled && distanceTravelled.length > 0) {
                    const uniqueDistanceTravelled = distanceTravelled.filter((elem, index, arr) => {
                        const elemDate = new Date(elem.updated_at).toDateString();
                        return arr.findIndex(item =>
                            item.milestone_id === elem.milestone_id &&
                            new Date(item.updated_at).toDateString() === elemDate
                        ) === index;
                    });
                    let dt = 0;
                    uniqueDistanceTravelled.forEach((elem) => {
                        if (elem?.payload?.dist_travelled_meter) {
                            dt += elem.payload.dist_travelled_meter
                        }
                    });
                    item.dist_travelled_meter = dt;
                }

                if (serviceRequestData[item.id]) {
                    if (serviceRequestData[item.id].assets?.length > 0 && filterCurrentAssets.length > 0) {
                        serviceRequestData[item.id].assets = filterCurrentAssets;
                    } else if (filterCurrentAssets.length > 0) {
                        serviceRequestData[item.id].assets = filterCurrentAssets;
                    }

                    if (serviceRequestData[item.id].milestones?.length > 0 && filterCurrentMilestones.length > 0) {
                        serviceRequestData[item.id].milestones = filterCurrentMilestones;
                    } else if (filterCurrentMilestones.length > 0) {
                        serviceRequestData[item.id].milestones = filterCurrentMilestones;
                    }
                }

                if (item?.agents?.length > 0 && ticket_payment_ratios?.length > 0) {
                    item.agents = item.agents.map((ag) => {
                        if (![TicketAgentRole.CLUSTER, TicketAgentRole.PSP, TicketAgentRole.SP, TicketAgentRole.WAREHOUSE_OWNER, TicketAgentRole.SUPERVISOR].includes(ag.role)) return ag;

                        const findCurrentAgent = ticket_payment_ratios.find((elem) => elem.agent_id === ag.agent.id);
                        if (findCurrentAgent) {
                            return {
                                ...ag,
                                ticket_payment_ratio: findCurrentAgent.ticket_payment_ratio
                            }
                        }
                        return ag;
                    })
                }


                if (filterCurrentMilestones?.length > 0) {
                    const total_dist_travelled_km = filterCurrentMilestones.reduce((acc, milestone) => {
                        if (parseFloat(milestone.total_dist_travelled_km) > 0) {
                            acc += parseFloat(milestone.total_dist_travelled_km);
                        }
                        return acc;
                    }, 0);
                    item.total_dist_travelled_km = total_dist_travelled_km;
                }

            });

            return {
                ...state,
                serviceRequestList: { ...state.serviceRequestList, data },
            };
        }

        case serviceRequest.GET_SERVICE_REQUEST_COUNT_SUCCESS: {
            return {
                ...state,
                serviceRequestCount: action.payload,
            };
        }

        case serviceRequest.GET_ELIGIBLE_AGENTS_SUCCESS: {
            return {
                ...state,
                eligibleAgents: action.payload,
            };
        }
        case serviceRequest.GET_TICKET_PROGRESS: {

            return {
                ...state,
                showProgress: true,
                ticketProgress: action.payload,
            }
        }

        case serviceRequest.SERVICE_REQUEST_TASK_UPDATE_SUCCESS: {
            const { ticket_id, milestone_id, ...taskData } = action.payload;

            const updatedMilestones = { ...state.serviceRequestMilestones };
            if (updatedMilestones[ticket_id]) {
                const milestoneIndex = updatedMilestones[ticket_id].findIndex(milestone => milestone.id === milestone_id);
                if (milestoneIndex !== -1) {
                    const updatedTasks = updatedMilestones[[ticket_id]][milestoneIndex].tasks.map(task =>
                        task.id === taskData.id ? { ...task, ...taskData } : task
                    );
                    updatedMilestones[ticket_id][milestoneIndex].tasks = updatedTasks;
                }
            }

            return {
                ...state,
                serviceRequestMilestones: updatedMilestones
            };
        }

        case serviceRequest.GET_SERVICE_REQUEST_BY_ID_SUCCESS: {
            const serviceRequest = action.payload;
            if (serviceRequest.inventory_ledger) serviceRequest.assets = serviceRequest.inventory_ledger;
            let data = state.serviceRequestList?.data || [];
            data = data.map(item => {
                if (item.id === serviceRequest.id) {
                    item.assets = serviceRequest.assets;
                    item.milestones = serviceRequest.milestones;
                }
                return item;
            });

            return {
                ...state,
                serviceRequestData: {
                    ...state.serviceRequestData,
                    [serviceRequest.id]: serviceRequest
                },
                serviceRequestList: {
                    ...state.serviceRequestList,
                    data
                }
            }
        }
        case serviceRequest.BULK_TICKET_EXAMPLE_FILE_SUCCESS: {
            const header = action.payload;
            return {
                ...state,
                csvHeader: header
            }
        }

        case serviceRequest.GET_SERVICE_REQUEST_PAYMENTS_SUCCESS: {
            return {
                ...state,
                serviceRequestPayments: {
                    ...state.serviceRequestPayments,
                    [action.payload.ticketId]: action.payload.data
                },
            }
        }
        case wallet.UPDATE_ADDITIONAL_PAYMENT_DETAILS: {
            const { data, ticketId } = action.payload;
            const currentPayments = state.serviceRequestPayments[ticketId] || [];

            const updatedPayments = currentPayments.map((elem) => {
                if (elem.id === data.id) {
                    let sts = elem.status;
                    if (data.status) sts = data.status;
                    return {
                        ...elem,
                        status: sts
                    };
                }
                return elem;
            });

            return {
                ...state,
                serviceRequestPayments: {
                    ...state.serviceRequestPayments,
                    [ticketId]: updatedPayments
                }
            };
        };

        case serviceRequest.GET_ALL_TICKET_PAYMENTS_SUCCESS: {
            const data = action.payload
            return {
                ...state,
                paymentList: data
            }
        }
        case serviceRequest.GET_SERVICE_REQUEST_MILESTONES_SUCCESS: {
            const { id, data, newLoggedUserData } = action.payload;
            const isLoginUserIsSP = newLoggedUserData.user.roles.find((elem) => elem.label === 'SP');
            if (isLoginUserIsSP) {
                const findAcceptedMilestones = data.filter((milestone) => milestone.agents.find((agent) => agent.agent_id === newLoggedUserData.user.id && agent.role === 'SP'));
                return {
                    ...state,
                    serviceRequestMilestones: {
                        ...state.serviceRequestMilestones,
                        [id]: findAcceptedMilestones
                    }
                }
            }
            return {
                ...state,
                serviceRequestMilestones: {
                    ...state.serviceRequestMilestones,
                    [id]: data
                }
            }
        }
        case serviceRequest.GET_SERVICE_REQUEST_BY_IDS_SUCCESS: {
            const data = action.payload;
            const serviceRequestData = state.serviceRequestData;
            data.forEach(item => {
                serviceRequestData[item.id] = item;
            });

            return {
                ...state,
                serviceRequestData,
            }
        }
        case serviceRequest.TRIGGERED_SERVICE_REQUEST_SUCCESS: {
            const { page, data } = action.payload;
            if (page > 1) {
                return {
                    ...state,
                    triggeredServiceRequestList: [...state.triggeredServiceRequestList, ...data],
                    isLastTriggeredServiceRequest: data.length === 0,
                }
            }
            return {
                ...state,
                triggeredServiceRequestList: data,
                isLastTriggeredServiceRequest: data.length === 0,
            }
        }
        case serviceRequest.UPDATE_TRIGGERED_SERVICE_REQUEST_SUCCESS: {
            const { add, remove } = action.payload;
            if (add) {
                return {
                    ...state,
                    triggeredServiceRequestList: [add, ...state.triggeredServiceRequestList],
                }
            }

            if (remove) {
                return {
                    ...state,
                    triggeredServiceRequestList: state.triggeredServiceRequestList.filter(item => item.id !== remove),
                }
            }

            return state;

        }
        case serviceRequest.GET_ELIGIBLE_ENGINEERS_SUCCESS: {
            return {
                ...state,
                eligibleEngineers: action.payload,
            }
        }
        case serviceRequest.GET_SERVICE_REQUEST_FILTER_LIST_SUCCESS: {
            const { data, q } = action.payload;

            const serviceRequestFilterList = state.serviceRequestFilterList;

            if (q) {
                return {
                    ...state,
                    serviceRequestFilterList: mergeUniqueByValue(serviceRequestFilterList, data)
                }
            }
            const customerInvoiceGenerated = [
                { key: 'yes', value: 'yes', label: 'Yes' },
                { key: 'no', value: 'no', label: 'No' }
            ];
            const amountRequested = [
                { key: 'yes', value: 'yes', label: 'Yes' },
                { key: 'no', value: 'no', label: 'No' }
            ];
            const ticketInvoiceGenerated = [
                { key: 'yes', value: 'yes', label: 'Invoice Raised' },
                { key: 'no', value: 'no', label: 'Invoice Not Raised' },
                { key: 'customer_billing_approved', value: 'customer_billing_approved', label: 'Customer Billing Approved' },
            ];
            const signOffStatus = [
                { key: 'accepted', value: 'accepted', label: 'Accepted' },
                { key: 'pending', value: 'pending', label: 'Pending' }
            ];
            const milestoneType = [
                { key: 'REMOTE_SUPPORT', value: 'REMOTE_SUPPORT', label: 'Remote Support' },
                { key: 'FIELD', value: 'FIELD', label: 'Field' },
                { key: 'INSTALLATION', value: 'INSTALLATION', label: 'Installation' },
                { key: 'RMA', value: 'RMA', label: 'RMA' },
                { key: 'LOGISTICS', value: 'LOGISTICS', label: 'Logistics' },
                { key: 'PAV', value: 'PAV', label: 'PAV' }
            ];
            const milestoneStatus = [
                { key: 'NEW', value: 'NEW', label: 'New' },
                { key: 'REMOTE_ASSIGNED', value: 'REMOTE_ASSIGNED', label: 'Remote Assigned' },
                { key: 'FIELD_ASSIGNED', value: 'FIELD_ASSIGNED', label: 'Field Assigned' },
                { key: 'BROADCASTED', value: 'BROADCASTED', label: 'Broadcasted' },
                { key: 'FIELD_ACCEPTED', value: 'FIELD_ACCEPTED', label: 'Field Accepted' },
                { key: 'TRACKING_REQUESTED', value: 'TRACKING_REQUESTED', label: 'Tracking Requested' },
                { key: 'ENGINEER_ON_THE_WAY', value: 'ENGINEER_ON_THE_WAY', label: 'Engineer On The Way' },
                { key: 'ENGINEER_AT_SITE', value: 'ENGINEER_AT_SITE', label: 'Engineer At Site' },
                { key: 'SITE_REVISIT_REQUIRED', value: 'SITE_REVISIT_REQUIRED', label: 'Site Revisit Required' },
                { key: 'ENGINEER_LEFT_SITE', value: 'ENGINEER_LEFT_SITE', label: 'Engineer Left Site' },
                { key: 'CUSTOMER_SIGN_OFF_ACCEPTED', value: 'CUSTOMER_SIGN_OFF_ACCEPTED', label: 'Customer Sign Off Accepted' },
                { key: 'CUSTOMER_SIGN_OFF_REJECTED', value: 'CUSTOMER_SIGN_OFF_REJECTED', label: 'Customer Sign Off Rejected' },
                { key: 'SIGN_OFF_REQUESTED', value: 'SIGN_OFF_REQUESTED', label: 'Sign Off Requested' },
                { key: 'SIGN_OFF_ACCEPTED', value: 'SIGN_OFF_ACCEPTED', label: 'Sign Off Accepted' },
                { key: 'SIGN_OFF_REJECTED', value: 'SIGN_OFF_REJECTED', label: 'Sign Off Rejected' },
                { key: 'SIGN_OFF_LOCKED', value: 'SIGN_OFF_LOCKED', label: 'Sign Off Locked' },
                { key: 'SIGN_OFF_APPROVAL_LOCKED', value: 'SIGN_OFF_APPROVAL_LOCKED', label: 'Sign Off Approval Locked' },
                { key: 'SITE_COMPLETED', value: 'SITE_COMPLETED', label: 'Site Completed' },
                { key: 'HARD_CLOSED', value: 'HARD_CLOSED', label: 'Hard Closed' },
                { key: 'ON_HOLD', value: 'ON_HOLD', label: 'On Hold' },
                { key: 'RMA_PENDING', value: 'RMA_PENDING', label: 'RMA Pending' },
                { key: 'RMA_UPDATED_DEVICE_CONSUMED_FULL', value: 'RMA_UPDATED_DEVICE_CONSUMED_FULL', label: 'RMA Updated : Device Consumed(Full)' },
                { key: 'RMA_UPDATED_PART_CONSUMED', value: 'RMA_UPDATED_PART_CONSUMED', label: 'RMA Updated : Part Consumed' },
                { key: 'RMA_UPDATED_NO_DEVICE_PARTS', value: 'RMA_UPDATED_NO_DEVICE_PARTS', label: 'RMA Updated : No Device/Parts' }
            ];
            const region_tier = [
                { key: 'TIER1', value: 'TIER1', label: 'Tier 1' },
                { key: 'TIER2', value: 'TIER2', label: 'Tier 2' },
                { key: 'TIER3', value: 'TIER3', label: 'Tier 3' },
                { key: 'TIER4', value: 'TIER4', label: 'Tier 4' }
            ];

            const base_payment_status = [
                { key: 'INITIATED', value: 'INITIATED', label: 'Initiated' },
                { key: 'PENDING', value: 'PENDING', label: 'Pending' },
                { key: 'SCHEDULED', value: 'SCHEDULED', label: 'Scheduled' },
                { key: 'PROCESSING', value: 'PROCESSING', label: 'Processing' },
                { key: 'SUCCESS', value: 'SUCCESS', label: 'Success' },
                { key: 'FAILURE', value: 'FAILURE', label: 'Failure' },
                { key: 'REJECTED', value: 'REJECTED', label: 'Rejected' },
                { key: 'HOLD', value: 'HOLD', label: 'Hold' },
            ];

            const remarksFilter = [
                { key: 'INCOMING_MISSED_CALL', value: 'INCOMING_MISSED_CALL', label: 'Incoming Missed Call' },
                { key: 'INCOMING_ANSWERED_CALL', value: 'INCOMING_ANSWERED_CALL', label: 'Incoming Answered Call' },
                { key: 'OUTGOING_CALL', value: 'OUTGOING_CALL', label: 'Outgoing Call' }
            ]

            const financialState = [
                { key: 'GOOD', value: 'GOOD', label: 'Within Budget' },
                { key: 'OVER_BUDGET', value: 'OVER_BUDGET', label: 'Over Budget' },
                { key: 'LOSS', value: 'LOSS', label: 'Loss' },
            ];

            const billingApprovalReason = billingApprovalReasonOptions;

            const billingRemarkInvoiceStatus = [
                { key: 'PENDING', value: 'PENDING', label: 'Pending' },
                { key: 'PARTIAL', value: 'PARTIAL', label: 'Partially Invoiced' },
                { key: 'INVOICED', value: 'INVOICED', label: 'Invoiced' },
            ];

            const billingRemarkHasAttachment = [
                { key: 'yes', value: 'yes', label: 'With Attachment' },
                { key: 'no', value: 'no', label: 'Without Attachment' },
            ];

            return {
                ...state,
                serviceRequestFilterList: {
                    ...state.serviceRequestFilterList,
                    ...data,
                    customerInvoiceGenerated,
                    signOffStatus,
                    milestoneType,
                    milestoneStatus,
                    ticketInvoiceGenerated,
                    region_tier,
                    amountRequested,
                    base_payment_status,
                    requestedAmountRange: { min: 0, max: 100000, selectedMin: 0, selectedMax: 0 },
                    remarksFilter,
                    financialState,
                    billingApprovalReason,
                    billingRemarkInvoiceStatus,
                    billingRemarkHasAttachment
                }
            }
        };
        case serviceRequest.SET_SERVICE_REQUEST_FILTER_PAYLOAD: {
            return {
                ...state,
                serviceRequestFilterPayload: action.payload
            }
        }

        case serviceRequest.GET_SERVICE_REQUEST_HISTORY_SUCCESS: {
            const { ticketId, data } = action.payload;
            return {
                ...state,
                serviceRequestHistory: {
                    ...state.serviceRequestHistory,
                    [ticketId]: data
                }
            }
        }

        case serviceRequest.SUPPORT_TRACK_TICKET_DETAILS_SUCCESS: {
            const { data, error } = action.payload;
            if (error) {
                return {
                    ...state,
                    supportTrackServiceRequestError: true
                }
            } else {
                return {
                    ...state,
                    supportTrackServiceRequest: data,
                    supportTrackServiceRequestError: false
                }

            }
        };

        case serviceRequest.SITE_COMPLETE_SUCCESS:
        case serviceRequest.REACHED_SITE_SERVICE_REQUEST_SUCCESS:
        case serviceRequest.GOING_TO_SITE_SERVICE_REQUEST_SUCCESS: {
            const { milestone, ticketId } = action.payload;
            let serviceRequestListData = state?.serviceRequestList?.data || [];
            serviceRequestListData = serviceRequestListData.map(item => {
                if (item.id === ticketId) {
                    item.milestones = item.milestones.map(m => {
                        if (m.id === milestone.id) {
                            return milestone;
                        }
                        return m;
                    })
                }
                return item;
            })
            return {
                ...state,
                serviceRequestList: { ...state.serviceRequestList, data: serviceRequestListData }
            }
        }

        case serviceRequest.SET_PAYMENT_FILTER_PAYLOAD: {
            return {
                ...state,
                paymentFilterPayload: action.payload
            }
        }
        case serviceRequest.REMOVE_ASSET_FROM_TICKET_SUCCESS: {
            const { ticketId, asset_id } = action.payload;

            const updatedServiceRequestData = { ...state.serviceRequestData };
            if (updatedServiceRequestData[ticketId]) {
                updatedServiceRequestData[ticketId] = {
                    ...updatedServiceRequestData[ticketId],
                    assets: updatedServiceRequestData[ticketId]?.assets?.filter(asset =>
                        asset.id !== asset_id && asset.asset_id !== asset_id
                    ) || []
                };
            }

            const updatedServiceRequestList = { ...state.serviceRequestList };
            if (updatedServiceRequestList.data) {
                updatedServiceRequestList.data = updatedServiceRequestList.data.map(item => {
                    if (item.id === ticketId) {
                        return {
                            ...item,
                            assets: item.assets?.filter(asset =>
                                asset.id !== asset_id && asset.asset_id !== asset_id
                            ) || []
                        };
                    }
                    return item;
                });
            }

            return {
                ...state,
                serviceRequestData: updatedServiceRequestData,
                serviceRequestList: updatedServiceRequestList
            };
        }

        case serviceRequest.GET_VISIT_HISTORY_SUCCESS: {
            const { ticketId, data } = action.payload;
            return {
                ...state,
                visitHistory: {
                    ...state.visitHistory,
                    [ticketId]: data
                }
            }
        }

        case serviceRequest.GET_SERVICE_REQUEST_COUNT_BY_STATUS_SUCCESS: {
            return {
                ...state,
                serviceRequestCountByStatus: action.payload
            }
        }

        case serviceRequest.SET_PAYMENT_COUNT: {
            let paymentList = state.paymentList;

            if (paymentList && paymentList.metadata) {
                paymentList = {
                    ...paymentList,
                    metadata: {
                        ...paymentList.metadata,
                        total_count: action.payload
                    }
                };
            }

            return {
                ...state,
                paymentCount: action.payload,
                paymentList: paymentList
            }
        }

        default:
            return {
                ...state,
            };
    }
};

export default serviceRequestReducer;




export const mergeUniqueByValue = (oldObj = {}, newObj = {}) => {
    const merged = {};

    const keys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

    for (const key of keys) {
        const oldValue = oldObj[key];
        const newValue = newObj[key];

        if (Array.isArray(oldValue) || Array.isArray(newValue)) {
            const oldArr = Array.isArray(oldValue) ? oldValue : [];
            const newArr = Array.isArray(newValue) ? newValue : [];

            const map = new Map();
            [...oldArr, ...newArr].forEach(item => {
                if (item?.value != null) map.set(item.value, item);
            });

            merged[key] = Array.from(map.values());
        } else {
            merged[key] = newValue !== undefined ? newValue : oldValue;
        }
    }

    return merged;
};
